import {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
// Import GeoJSON type (ensure @types/geojson is installed)
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import type { GeoJsonObject } from 'geojson';
import {
  Box,
  ButtonBase,
  Checkbox,
  Alert,
  Stack,
  Tooltip,
  ToggleButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  IconButton,
  Typography,
  Drawer,
  Divider,
  useTheme,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import MapIcon from '@mui/icons-material/Map';
import {useSceneWindow} from '../scene/useSceneWindow';
import type {SceneSource} from '../scene/types';
import LanguageIcon from '@mui/icons-material/Language';
import LayersIcon from "@mui/icons-material/Layers";
import EditNoteIcon from "@mui/icons-material/EditNote";
import GridOnIcon from "@mui/icons-material/GridOn";
import StraightenIcon from "@mui/icons-material/Straighten";
import PlaceIcon from "@mui/icons-material/Place";
import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import ViewTimelineIcon from "@mui/icons-material/ViewTimeline";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import AreaChartIcon from "@mui/icons-material/AreaChart";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { vscodeTheme } from "@uiw/react-json-view/vscode";
import { lightTheme } from "@uiw/react-json-view/light";

import VideoPlayer, { type VideoPlayerProps, type VideoPlayerRef } from "../components/VideoPlayer";
import PlayerLayoutControl from "../components/PlayerLayoutControl";
import { getPlayerGrid, persistPlayersPerRow, readPlayersPerRow } from "../utils/playerGridLayout";
import { persistPlayerSelection, readPlayerSelection, reconcilePlayerSelection } from "../utils/playerSelection";
import { playerSurfaceColors } from "../theme";
import VideoOverlay from "../components/VideoOverlay";
import OverlayEditorSessionPanel from "../components/OverlayEditorSessionPanel";
import StreamState from "../components/StreamState";
import KlvWindow from "../components/KlvWindow";
import MapWindow, {
  MapPlatformDescriptor,
  type MapMarkerMode,
  type MapMarkerPosition,
  type MapMarkerToggleEvent,
  type MapViewportDescriptor,
} from "../map/MapWindow";
import StreamInfoDialog from "../components/StreamInfoDialog";
import StreamEventsDrawer from "../components/StreamEventsDrawer";
import StreamBitrateChart from "../components/StreamBitrateChart";
import StreamBitrateAnalyticsChart, { type BitrateHistoryRange } from "../components/StreamBitrateAnalyticsChart";
import StreamEventsAnalyticsChart from "../components/StreamEventsAnalyticsChart";
// import StreamEventFiltersDialog, {
//   createDefaultStreamEventErrorFilterSettings,
//   resolveStreamEventErrorFilterStorageKey,
//   type StreamEventErrorFilterSettings,
// } from "../components/StreamEventFiltersDialog";
import ExportImageDlg from "../components/ExportImageDlg";
import StreamCalendar from "../components/StreamCalendar";
import { useAuth } from "../context/AuthContext";
import {
  subscribeToPlatformListChanges,
  subscribeToStreamListChanges,
  useAppContext,
} from "../context/AppContext";
import { useStreamsState } from "../context/StreamsStateContext";
import { useStreamDetectionInfo } from "../hooks/useStreamDetectionInfo";
import type { PlatformSummary } from "../types/platform";
import type { AnnotationRenderItem, AnnotationStorage, AnnotationSummary } from "../types/annotation";
import { openExternalPath } from "../utils/externalLinks";
import {
  persistPlayerOverlayLayout,
  readPlayerOverlayLayout,
  type StoredPlayerOverlayLayout,
} from "../utils/playerOverlayLayout";
import { resolvePlaybackReceiverBufferMs } from "../utils/playerPlaybackPolicy";
import { createPlayerOverlayPersistence } from "../utils/playerOverlayPersistence";

// ---------- Types ----------
// Raw packet wrapper coming from VideoPlayer (timestamped)
interface RawKlvPacket {
  time: Date; // timestamp supplied by player event (ms precision)
  klv: Record<string, unknown>;
  sceneSequence?: number;
  sceneGeneration?: string;
}

type OverlayMarkerSyncState = {
  enabled: boolean;
  master: boolean;
  available: boolean;
  point: { x: number; y: number } | null;
  latitude: number | null;
  longitude: number | null;
};

// Lightweight derived view we compute lazily / throttled for the map


type StreamSummary = {
  id: string;
  sensor: string;
  type?: string;
  platformId?: string;
  platformName?: string;
  url: string;
  payloadType?: string;
  active: boolean;
  description?: string;
  mode?: string | null;
  lowLatency?: boolean;
  cacheMs?: number;
  targets?: Array<{
    engines?: Array<{
      type?: string;
      config?: Record<string, unknown> | null;
    }> | null;
  }> | null;
};

type AnnotationResponse = {
  id?: unknown;
  name?: unknown;
  description?: unknown;
  storage?: unknown;
  active?: unknown;
  tags?: unknown;
  userGroups?: unknown;
  featureCollection?: unknown;
  bbox?: unknown;
  restricted?: unknown;
};

// ---------- Constants ----------
const THROTTLE_INTERVAL_MS = 300;
const ANNOTATION_QUERY_REFRESH_INTERVAL_MS = 5000;
const ANNOTATION_FETCH_REFETCH_INTERVAL_MS = 15000;
const ANNOTATION_QUERY_TRAIL_WINDOW_MS = 5 * 60 * 1000;
const ANNOTATION_QUERY_PADDING_RATIO = 0.35;
const ANNOTATION_QUERY_MIN_PAD_DEGREES = 0.01;
const ANNOTATION_QUERY_COMPARISON_TOLERANCE = 0.002;
const DEFAULT_PLATFORM_TYPE = "plane";
const DEFAULT_PLATFORM_COLOR = "#2563eb";
const DEFAULT_OVERLAY_VIEWPORT = { width: 1280, height: 720 };
const SNAPSHOT_FALLBACK_NAME = "snapshot";
const LOCAL_ANNOTATIONS_STORAGE_KEY = "sky-view.annotations.local";
const EMPTY_ANNOTATION_SUMMARIES: AnnotationSummary[] = [];
const EMPTY_ANNOTATION_ITEMS: AnnotationRenderItem[] = [];
const BITRATE_RANGE_STORAGE_KEY = "sky-view.bitrate.range";
const BITRATE_REFRESH_STORAGE_KEY = "sky-view.bitrate.refresh";
const EVENTS_RANGE_STORAGE_KEY = "sky-view.stream-events-chart.range";
const EVENTS_REFRESH_STORAGE_KEY = "sky-view.stream-events-chart.refresh";
const isDataPayload = (payloadType?: string | null) =>
  (payloadType ?? "").trim().toLowerCase() === "data";
type BitrateRangeOption = "latest" | BitrateHistoryRange;
const BITRATE_RANGE_OPTIONS: { value: BitrateRangeOption; label: string }[] = [
  { value: "latest", label: "Latest" },
  { value: "5m", label: "Last 5 min" },
  { value: "15m", label: "Last 15 min" },
  { value: "1h", label: "Last hour" },
  { value: "3h", label: "Last 3 hours" },
  { value: "6h", label: "Last 6 hours" },
  { value: "12h", label: "Last 12 hours" },
  { value: "24h", label: "Last 24 hours" },
  { value: "all", label: "All" },
];
const BITRATE_REFRESH_OPTIONS: { value: number; label: string }[] = [
  { value: 5000, label: "5 sec" },
  { value: 10000, label: "10 sec" },
];

const sanitizeFileName = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const sanitized = trimmed.replace(/[^a-zA-Z0-9._-]/g, "_");
  return sanitized || null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const toTrimmedString = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const toStringList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
          .filter((entry) => entry.length > 0),
      ),
    );
  }

  if (typeof value === "string") {
    return Array.from(
      new Set(
        value
          .split(",")
          .map((entry) => entry.trim())
          .filter((entry) => entry.length > 0),
      ),
    );
  }

  return [];
};

const isAnnotationStorage = (value: unknown): value is AnnotationStorage =>
  value === "server" || value === "local";

const normalizeAnnotationSummary = (
  annotation: AnnotationResponse,
  fallbackStorage: AnnotationStorage,
): AnnotationSummary | null => {
  const id = toTrimmedString(annotation.id);
  const name = toTrimmedString(annotation.name);

  if (!id || !name) {
    return null;
  }

  return {
    id,
    name,
    description: typeof annotation.description === "string" ? annotation.description : "",
    storage: isAnnotationStorage(annotation.storage) ? annotation.storage : fallbackStorage,
    active: annotation.active !== false,
    tags: toStringList(annotation.tags),
    userGroups: toStringList(annotation.userGroups),
    featureCollection: annotation.featureCollection ?? null,
    bbox: Array.isArray(annotation.bbox)
      ? annotation.bbox.filter((value): value is number => typeof value === "number" && Number.isFinite(value))
      : [],
    restricted: annotation.restricted === true,
  };
};

const cloneGeoJson = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const readLocalPlayerAnnotations = (): AnnotationSummary[] => {
  if (typeof window === "undefined") {
    return [];
  }

  const raw = window.localStorage.getItem(LOCAL_ANNOTATIONS_STORAGE_KEY);
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as AnnotationResponse[];
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map((annotation) => normalizeAnnotationSummary(annotation, "local"))
      .filter((annotation): annotation is AnnotationSummary => Boolean(annotation));
  } catch {
    return [];
  }
};

const toAnnotationRenderItem = (annotation: AnnotationSummary): AnnotationRenderItem | null => {
  if (!annotation.active || !isRecord(annotation.featureCollection)) {
    return null;
  }

  const type = toTrimmedString(annotation.featureCollection.type);
  if (!type) {
    return null;
  }

  return {
    id: `annotation:${annotation.storage}:${annotation.id}`,
    name: annotation.name,
    geojson: cloneGeoJson(annotation.featureCollection as GeoJsonObject),
  };
};

type GeoPoint = {
  latitude: number;
  longitude: number;
};

type GeoBBox = [number, number, number, number];

type TimedGeoPoint = {
  timestamp: number;
  point: GeoPoint;
};

const getNumericField = (
  fields: Record<string, unknown> | null | undefined,
  ...keys: string[]
) => {
  if (!fields) {
    return null;
  }

  for (const key of keys) {
    const value = fields[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === "string") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }

  return null;
};

const resolveGeoPoint = (
  fields: Record<string, unknown> | null | undefined,
  latitudeKeys: string[],
  longitudeKeys: string[],
): GeoPoint | null => {
  const latitude = getNumericField(fields, ...latitudeKeys);
  const longitude = getNumericField(fields, ...longitudeKeys);
  if (latitude === null || longitude === null) {
    return null;
  }

  return { latitude, longitude };
};

const resolveFrameCenter = (packet: Record<string, unknown> | null | undefined) =>
  resolveGeoPoint(
    packet,
    ["23", "FrameCenterLatitude", "Frame Center Latitude"],
    ["24", "FrameCenterLongitude", "Frame Center Longitude"],
  );

const resolveSensorPosition = (packet: Record<string, unknown> | null | undefined) =>
  resolveGeoPoint(
    packet,
    ["13", "SensorLatitude", "Sensor Latitude"],
    ["14", "SensorLongitude", "Sensor Longitude"],
  );

const resolveOffsetCornerPoint = (
  packet: Record<string, unknown> | null | undefined,
  frameCenter: GeoPoint | null,
  latitudeKeys: string[],
  longitudeKeys: string[],
) => {
  if (!frameCenter) {
    return null;
  }

  const latitudeOffset = getNumericField(packet, ...latitudeKeys);
  const longitudeOffset = getNumericField(packet, ...longitudeKeys);
  if (latitudeOffset === null || longitudeOffset === null) {
    return null;
  }

  return {
    latitude: frameCenter.latitude + latitudeOffset,
    longitude: frameCenter.longitude + longitudeOffset,
  };
};

const resolveTerrainCorners = (packet: Record<string, unknown> | null | undefined) => {
  const frameCenter = resolveFrameCenter(packet);
  const fullCorners = [
    resolveGeoPoint(
      packet,
      ["82", "CornerLatitudePoint1Full", "Corner Latitude Point 1 (Full)"],
      ["83", "CornerLongitudePoint1Full", "Corner Longitude Point 1 (Full)"],
    ),
    resolveGeoPoint(
      packet,
      ["84", "CornerLatitudePoint2Full", "Corner Latitude Point 2 (Full)"],
      ["85", "CornerLongitudePoint2Full", "Corner Longitude Point 2 (Full)"],
    ),
    resolveGeoPoint(
      packet,
      ["86", "CornerLatitudePoint3Full", "Corner Latitude Point 3 (Full)"],
      ["87", "CornerLongitudePoint3Full", "Corner Longitude Point 3 (Full)"],
    ),
    resolveGeoPoint(
      packet,
      ["88", "CornerLatitudePoint4Full", "Corner Latitude Point 4 (Full)"],
      ["89", "CornerLongitudePoint4Full", "Corner Longitude Point 4 (Full)"],
    ),
  ];

  if (fullCorners.every(Boolean)) {
    return fullCorners as GeoPoint[];
  }

  const offsetCorners = [
    resolveOffsetCornerPoint(
      packet,
      frameCenter,
      ["26", "OffsetCornerLatitudePoint1", "Offset Corner Latitude Point 1"],
      ["27", "OffsetCornerLongitudePoint1", "Offset Corner Longitude Point 1"],
    ),
    resolveOffsetCornerPoint(
      packet,
      frameCenter,
      ["28", "OffsetCornerLatitudePoint2", "Offset Corner Latitude Point 2"],
      ["29", "OffsetCornerLongitudePoint2", "Offset Corner Longitude Point 2"],
    ),
    resolveOffsetCornerPoint(
      packet,
      frameCenter,
      ["30", "OffsetCornerLatitudePoint3", "Offset Corner Latitude Point 3"],
      ["31", "OffsetCornerLongitudePoint3", "Offset Corner Longitude Point 3"],
    ),
    resolveOffsetCornerPoint(
      packet,
      frameCenter,
      ["32", "OffsetCornerLatitudePoint4", "Offset Corner Latitude Point 4"],
      ["33", "OffsetCornerLongitudePoint4", "Offset Corner Longitude Point 4"],
    ),
  ];

  if (offsetCorners.every(Boolean)) {
    return offsetCorners as GeoPoint[];
  }

  return null;
};

const bboxFromPoint = (point: GeoPoint): GeoBBox => [
  point.longitude,
  point.latitude,
  point.longitude,
  point.latitude,
];

const extendBBoxWithPoint = (bbox: GeoBBox, point: GeoPoint): GeoBBox => [
  Math.min(bbox[0], point.longitude),
  Math.min(bbox[1], point.latitude),
  Math.max(bbox[2], point.longitude),
  Math.max(bbox[3], point.latitude),
];

const bboxFromPoints = (points: GeoPoint[]): GeoBBox | null => {
  if (points.length === 0) {
    return null;
  }

  let bbox = bboxFromPoint(points[0]);
  for (const point of points.slice(1)) {
    bbox = extendBBoxWithPoint(bbox, point);
  }
  return bbox;
};

const unionBBox = (left: GeoBBox | null, right: GeoBBox | null): GeoBBox | null => {
  if (!left) {
    return right ? ([...right] as GeoBBox) : null;
  }
  if (!right) {
    return [...left] as GeoBBox;
  }
  return [
    Math.min(left[0], right[0]),
    Math.min(left[1], right[1]),
    Math.max(left[2], right[2]),
    Math.max(left[3], right[3]),
  ];
};

const expandBBox = (bbox: GeoBBox): GeoBBox => {
  const lonSpan = Math.max(bbox[2] - bbox[0], ANNOTATION_QUERY_MIN_PAD_DEGREES);
  const latSpan = Math.max(bbox[3] - bbox[1], ANNOTATION_QUERY_MIN_PAD_DEGREES);
  const lonPadding = Math.max(lonSpan * ANNOTATION_QUERY_PADDING_RATIO, ANNOTATION_QUERY_MIN_PAD_DEGREES);
  const latPadding = Math.max(latSpan * ANNOTATION_QUERY_PADDING_RATIO, ANNOTATION_QUERY_MIN_PAD_DEGREES);

  return [
    bbox[0] - lonPadding,
    Math.max(-90, bbox[1] - latPadding),
    bbox[2] + lonPadding,
    Math.min(90, bbox[3] + latPadding),
  ];
};

const bboxesRoughlyEqual = (left: GeoBBox | null, right: GeoBBox | null): boolean => {
  if (left === right) {
    return true;
  }
  if (!left || !right) {
    return false;
  }
  return left.every((value, index) => Math.abs(value - right[index]) <= ANNOTATION_QUERY_COMPARISON_TOLERANCE);
};

const serializeBBox = (bbox: GeoBBox | null): string | null => {
  if (!bbox) {
    return null;
  }
  return bbox.map((value) => value.toFixed(4)).join(",");
};

const annotationIntersectsBBox = (annotationBBox: number[], queryBBox: GeoBBox | null): boolean => {
  if (!queryBBox) {
    return true;
  }
  if (annotationBBox.length !== 4) {
    return false;
  }
  return (
    annotationBBox[0] <= queryBBox[2] &&
    annotationBBox[2] >= queryBBox[0] &&
    annotationBBox[1] <= queryBBox[3] &&
    annotationBBox[3] >= queryBBox[1]
  );
};

const collectAnnotationGeoPoints = (packet: Record<string, unknown> | null | undefined): GeoPoint[] => {
  if (!packet) {
    return [];
  }

  const points: GeoPoint[] = [];
  const sensor = resolveSensorPosition(packet);
  if (sensor) {
    points.push(sensor);
  }
  const frameCenter = resolveFrameCenter(packet);
  if (frameCenter) {
    points.push(frameCenter);
  }
  const corners = resolveTerrainCorners(packet);
  if (corners) {
    points.push(...corners);
  }
  return points;
};

const ACTIVE_PLAYER_STATES = new Set(["connected", "running"]);
const INACTIVE_PLAYER_STATES = new Set([
  "disconnected",
  "failed",
  "closed",
  "offline",
  "timeout",
  "stopped",
  "error",
  "starting",
]);

const PLATFORM_TYPE_ALIASES: Record<string, string> = {
  plane: "plane",
  airplane: "plane",
  aircraft: "plane",
  "fixed-wing": "plane",
  "fixed wing": "plane",
  fixedwing: "plane",
  helicopter: "helicopter",
  heli: "helicopter",
  helo: "helicopter",
  rotorcraft: "helicopter",
  quadcopter: "quadcopter",
  quad: "quadcopter",
  drone: "quadcopter",
  uav: "quadcopter",
  multicopter: "quadcopter",
};

const sanitizePlatformTypeKey = (value: string) => value.replace(/[^a-z0-9]/g, "");

const resolvePlatformTypeForMap = (rawType: string | null | undefined): string | null => {
  if (!rawType) {
    return null;
  }
  const trimmed = rawType.trim();
  if (!trimmed) {
    return null;
  }
  const lower = trimmed.toLowerCase();
  if (PLATFORM_TYPE_ALIASES[lower]) {
    return PLATFORM_TYPE_ALIASES[lower];
  }
  const compact = sanitizePlatformTypeKey(lower);
  if (compact && PLATFORM_TYPE_ALIASES[compact]) {
    return PLATFORM_TYPE_ALIASES[compact];
  }
  return lower;
};

const normalizePlatformColor = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const hexBody = trimmed.startsWith("#") ? trimmed.slice(1) : trimmed;
  const normalized = hexBody.replace(/[^a-f0-9]/gi, "").toLowerCase();
  if (!normalized) {
    return null;
  }
  if (normalized.length === 3 || normalized.length === 6 || normalized.length === 8) {
    return `#${normalized}`;
  }
  return null;
};

const createDefaultOverlayLayout = (viewport: { width: number; height: number }): StoredPlayerOverlayLayout => {
  const width = Math.max(1, viewport.width);
  const height = Math.max(1, viewport.height);
  const minDimension = Math.max(1, Math.min(width, height));
  const compassInset = 16;
  const compassSize = 120;
  const compassX = Math.max(compassInset, width - compassSize - compassInset);

  return {
    placeholder: null,
    headingDisplay: {
      x: compassX / width,
      y: compassInset / height,
      size: compassSize / width,
      fill: "#00ff7f",
      fontSize: 14 / minDimension,
      rotation: 0,
    },
    vmti: [],
    texts: [],
    dataTexts: [],
    hudDisplayMode: "all",
    rulerEnabled: false,
    rulerUnit: "meters",
    markerEnabled: false,
    markerMaster: true,
    markerPoint: null,
    markerCoordinate: null,
  };
};

const resolveOverlayViewport = (videoSize: { width: number; height: number }) => ({
  width: videoSize.width > 0 ? videoSize.width : DEFAULT_OVERLAY_VIEWPORT.width,
  height: videoSize.height > 0 ? videoSize.height : DEFAULT_OVERLAY_VIEWPORT.height,
});

const createCenteredMarkerPoint = (viewport: { width: number; height: number }) => ({
  x: Math.max(1, viewport.width) * 0.5,
  y: Math.max(1, viewport.height) * 0.5,
});

const isRecastOnlyMode = (mode?: string | null) => (mode ?? "").trim().toLowerCase() === "recast";

// ---------- Component ----------

// Every tile owns its packets and stable WebRTC callbacks. Changing focus or the
// grid must not reconnect players or send another stream's KLV to page tools.
const PlayerTile = ({ stream, streamState, focused, multiple, playerProps, overlayProps, editing, onEditorSurface,
  maxHeight, onFocus, onData, onState, playerRef, surfaceRef }: {
  stream: StreamSummary;
  streamState: Parameters<typeof StreamState>[0]["state"];
  focused: boolean;
  multiple: boolean;
  playerProps: VideoPlayerProps;
  overlayProps: Omit<Parameters<typeof VideoOverlay>[0], "klvPacket">;
  editing: boolean;
  onEditorSurface: (streamId: string, node: HTMLDivElement | null) => void;
  maxHeight?: number;
  onFocus: () => void;
  onData: NonNullable<VideoPlayerProps["onDataReceive"]>;
  onState: NonNullable<VideoPlayerProps["onStateChange"]>;
  playerRef: { current: VideoPlayerRef | null };
  surfaceRef: (node: HTMLDivElement | null) => void;
}) => {
  const [packet, setPacket] = useState<RawKlvPacket | null>(null);
  const latestPacket = useRef<RawKlvPacket | null>(null);
  const latestState = useRef("starting");
  const localPlayerRef = useRef<VideoPlayerRef | null>(null);
  const callbacks = useRef({ focused, editing, onData, onState, overlayProps });
  callbacks.current = { focused, editing, onData, onState, overlayProps };
  const handleEditorSurface = useCallback((node: HTMLDivElement | null) => {
    onEditorSurface(stream.id, node);
  }, [onEditorSurface, stream.id]);
  // Presentation renderers can publish during resize and teardown. Only the
  // focused viewer may save, and it yields ownership while the editor is open.
  const saveViewerLayout = useCallback((layout: StoredPlayerOverlayLayout) => {
    const current = callbacks.current;
    if (current.focused && !current.editing) current.overlayProps.onLayoutChange?.(layout);
  }, []);
  const emitMarkerState = useCallback((state: OverlayMarkerSyncState) => {
    const current = callbacks.current;
    if (current.focused && !current.editing) current.overlayProps.onMarkerStateChange?.(state);
  }, []);
  const receiveData = useCallback((next: { time: Date; klv: unknown }) => {
    if (!next.klv || typeof next.klv !== "object") return;
    const nextPacket = next as RawKlvPacket;
    latestPacket.current = nextPacket;
    setPacket(nextPacket);
    if (callbacks.current.focused) callbacks.current.onData(next);
  }, []);
  const receiveState = useCallback((state: string) => {
    latestState.current = state;
    if (INACTIVE_PLAYER_STATES.has(state.toLowerCase())) {
      latestPacket.current = null;
      setPacket(null);
    }
    if (callbacks.current.focused) callbacks.current.onState(state);
  }, []);
  useEffect(() => {
    if (!focused) return;
    const handle = localPlayerRef.current;
    playerRef.current = handle;
    callbacks.current.onState(latestState.current);
    if (latestPacket.current) callbacks.current.onData(latestPacket.current);
    return () => { if (playerRef.current === handle) playerRef.current = null; };
  }, [focused, playerRef]);

  return (
    <Box sx={{ minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Box onClick={onFocus}
        sx={{ minHeight: 0, display: "flex", alignItems: "flex-start", justifyContent: "center" }}>
        <VideoPlayer {...playerProps} ref={localPlayerRef} maxHeight={maxHeight}
          header={multiple ? (
            <ButtonBase onClick={onFocus} aria-pressed={focused}
              aria-label={`Use tools for ${stream.sensor}`}
              sx={{ width: "100%", height: 32, px: 1, gap: 1, justifyContent: "flex-start", borderRadius: 1,
                bgcolor: focused ? "action.selected" : "transparent", color: focused ? "primary.main" : "text.secondary" }}>
              <StreamState state={streamState} size={10} />
              <Typography noWrap variant="caption" sx={{ minWidth: 0 }}>{stream.platformName ? `${stream.platformName} / ${stream.sensor}` : stream.sensor}</Typography>
            </ButtonBase>
          ) : undefined}
          highlighted={multiple && focused} surfaceRef={focused ? surfaceRef : undefined}
          onDataReceive={receiveData} onStateChange={receiveState}
          overlay={focused && editing ? (
            <Box ref={handleEditorSurface}
              sx={{ position: "absolute", inset: 0, zIndex: 2, pointerEvents: "auto" }} />
          ) : (
            <VideoOverlay {...overlayProps} klvPacket={packet}
              onLayoutChange={saveViewerLayout} onMarkerStateChange={emitMarkerState} />
          )} />
      </Box>
    </Box>
  );
};

const PlayerPage = () => {
  const { authorizedFetch, resolveApiPath, user, tokens, userManagementEnabled } = useAuth();
  const { iceServers, isMaxClientsExceeded, serverConfig } = useAppContext();
  const { getStreamState } = useStreamsState();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const analyticsEnabled = serverConfig.analyticsEnabled;
  const canViewDetectionInfo = !userManagementEnabled || Boolean(user) || Boolean(tokens?.accessToken);
  const {
    detectionStream,
    detectionData,
    detectionStateLabel,
    showDetectingSpinner,
    isDetectionLoading,
    detectionErrorMessage,
    openDetection,
    closeDetection,
  } = useStreamDetectionInfo({ enabled: canViewDetectionInfo });
  const jsonViewTheme = useMemo(
    () => (theme.palette.mode === "dark" ? vscodeTheme : lightTheme),
    [theme.palette.mode],
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedStreamId = useMemo(() => {
    const fromQuery = searchParams.get("streamId");
    if (!fromQuery) {
      return null;
    }
    const trimmed = fromQuery.trim();
    return trimmed ? trimmed : null;
  }, [searchParams]);

  const {
    data: streams,
    isLoading: isStreamsLoading,
    isError: isStreamsError,
    isFetching: isStreamsFetching,
    isFetchedAfterMount: isStreamsFetchedAfterMount,
  } = useQuery<StreamSummary[]>({
    queryKey: ["streams"],
    refetchOnMount: "always",
    queryFn: async () => {
      const response = await authorizedFetch("/api/streams");
      const payload = (await response.json().catch(() => null)) as {
        streams?: StreamSummary[];
        error?: string;
      } | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to load streams");
      }
      return payload?.streams ?? [];
    },
  });

  const { data: platforms } = useQuery<PlatformSummary[]>({
    queryKey: ["platforms"],
    queryFn: async () => {
      const response = await authorizedFetch("/api/platforms");
      const payload = (await response.json().catch(() => null)) as {
        platforms?: PlatformSummary[];
        error?: string;
      } | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to load platforms");
      }
      return payload?.platforms ?? [];
    },
  });

  useEffect(() => {
    const unsubscribeStreams = subscribeToStreamListChanges(() => {
      void queryClient.invalidateQueries({ queryKey: ["streams"] });
    });
    const unsubscribePlatforms = subscribeToPlatformListChanges(() => {
      void queryClient.invalidateQueries({ queryKey: ["platforms"] });
      void queryClient.invalidateQueries({ queryKey: ["streams"] });
    });
    return () => {
      unsubscribeStreams();
      unsubscribePlatforms();
    };
  }, [queryClient]);

  const [activeStreamId, setActiveStreamId] = useState<string | null>(null);
  const [selectedStreamIds, setSelectedStreamIds] = useState<string[]>([]);
  const selectionInitializedRef = useRef(false);
  const [selectionReady, setSelectionReady] = useState(false);
  const [playersPerRow, setPlayersPerRow] = useState(readPlayersPerRow);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [gridHeight, setGridHeight] = useState(480);
  // When changing streams we want a two-phase transition: first remove old platform,
  // then (on next tick) start/open the new stream so MapWindow sees a diff with an
  // empty platforms list and emits remove-platform before add-platform.
  const pendingStreamIdRef = useRef<string | null>(null);
  const [isKlvWindowOpen, setIsKlvWindowOpen] = useState<boolean>(false);
  // Last klv packet (as received)
  const [rawKlvPacket, setRawKlvPacket] = useState<RawKlvPacket | null>(null);
  // Throttled KLV packet for map/platform updates
  const [throttledKlv, setThrottledKlv] = useState<RawKlvPacket | null>(null);
  const lastThrottleUpdateRef = useRef<number>(Date.now());
  const pendingThrottleRef = useRef<number | null>(null);
  const sceneRun = useRef(0);
  const sceneSequence = useRef(0);
  const sceneStreamIdentity = useRef<string | null>(null);
  const [isMapWindowOpen, setIsMapWindowOpen] = useState<boolean>(false);
  const [isOverlayEditorOpen, setIsOverlayEditorOpen] = useState(false);
  const overlayEditorOpenRef = useRef(isOverlayEditorOpen);
  overlayEditorOpenRef.current = isOverlayEditorOpen;
  const [localAnnotations, setLocalAnnotations] = useState<AnnotationSummary[]>(() => readLocalPlayerAnnotations());
  const [annotationViewport, setAnnotationViewport] = useState<MapViewportDescriptor | null>(null);
  const [annotationQueryBBox, setAnnotationQueryBBox] = useState<GeoBBox | null>(null);
  const annotationTrailRef = useRef<TimedGeoPoint[]>([]);
  const [overlayLayout, setOverlayLayout] = useState<StoredPlayerOverlayLayout | null>(() =>
    readPlayerOverlayLayout(),
  );
  const [isGridEnabled, setIsGridEnabled] = useState<boolean>(
    () => readPlayerOverlayLayout()?.layerEnabledStates?.grid ?? false,
  );
  const [isRulerEnabled, setIsRulerEnabled] = useState<boolean>(
    () => readPlayerOverlayLayout()?.rulerEnabled ?? false,
  );
  const [isMarkerEnabled, setIsMarkerEnabled] = useState<boolean>(
    () => readPlayerOverlayLayout()?.markerEnabled ?? false,
  );
  const [overlayMarkerState, setOverlayMarkerState] = useState<OverlayMarkerSyncState | null>(null);
  const [playbackStream, setPlaybackStream] = useState<StreamSummary | null>(
    null,
  );
  const [isBitrateChartOpen, setIsBitrateChartOpen] = useState(false);
  const [isEventsChartOpen, setIsEventsChartOpen] = useState(false);
  // const [isEventsFilterDialogOpen, setIsEventsFilterDialogOpen] = useState(false);
  // const [eventsErrorFilterSettings, setEventsErrorFilterSettings] = useState<StreamEventErrorFilterSettings>(
  //   createDefaultStreamEventErrorFilterSettings,
  // );
  const [bitrateRange, setBitrateRange] = useState<BitrateRangeOption>(() => {
    if (typeof window === "undefined") {
      return "latest";
    }
    const stored = window.localStorage.getItem(BITRATE_RANGE_STORAGE_KEY);
    const normalized = stored?.trim().toLowerCase() as BitrateRangeOption | undefined;
    return BITRATE_RANGE_OPTIONS.some((option) => option.value === normalized) ? normalized! : "latest";
  });
  const [bitrateRefreshMs, setBitrateRefreshMs] = useState<number>(() => {
    if (typeof window === "undefined") {
      return 5000;
    }
    const stored = window.localStorage.getItem(BITRATE_REFRESH_STORAGE_KEY);
    const parsed = stored ? Number.parseInt(stored, 10) : NaN;
    return BITRATE_REFRESH_OPTIONS.some((option) => option.value === parsed) ? parsed : 5000;
  });
  const [eventsRange, setEventsRange] = useState<BitrateRangeOption>(() => {
    if (typeof window === "undefined") {
      return "latest";
    }
    const stored = window.localStorage.getItem(EVENTS_RANGE_STORAGE_KEY);
    const normalized = stored?.trim().toLowerCase() as BitrateRangeOption | undefined;
    return BITRATE_RANGE_OPTIONS.some((option) => option.value === normalized) ? normalized! : "latest";
  });
  const [eventsRefreshMs, setEventsRefreshMs] = useState<number>(() => {
    if (typeof window === "undefined") {
      return 5000;
    }
    const stored = window.localStorage.getItem(EVENTS_REFRESH_STORAGE_KEY);
    const parsed = stored ? Number.parseInt(stored, 10) : NaN;
    return BITRATE_REFRESH_OPTIONS.some((option) => option.value === parsed) ? parsed : 5000;
  });
 // const eventsFilterStorageKey = useMemo(() => resolveStreamEventErrorFilterStorageKey(user), [user]);
  const [videoContainer, setVideoContainer] = useState<HTMLDivElement | null>(null);
  const handleVideoContainer = useCallback((node: HTMLDivElement | null) => {
    setVideoContainer(node);
  }, []);
  const [overlayEditorSurface, setOverlayEditorSurface] = useState<{
    streamId: string;
    element: HTMLDivElement;
  } | null>(null);
  const [videoSize, setVideoSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [timelineStreamId, setTimelineStreamId] = useState<string | null>(null);
  const [calendarStreamId, setCalendarStreamId] = useState<string | null>(null);
  const activeStreamIdRef = useRef<string | null>(null);
  const platformGeometryRef = useRef<GeoJsonObject | null>(null);
  // Keep latest KLV packet in a ref (independent of throttling) so we can
  // initialize geometry when a new stream starts without recreating geometry
  // on every throttled update.
  const latestKlvRef = useRef<RawKlvPacket | null>(null);
  const videoPlayerRef = useRef<VideoPlayerRef | null>(null);
  const [snapshotDraft, setSnapshotDraft] = useState<{ dataUrl: string; defaultName: string; klv?: Record<string, unknown> | null } | null>(null);
  const annotationsEnabled = overlayLayout?.layerEnabledStates?.annotations === true;
  const annotationQueryBBoxParam = useMemo(() => serializeBBox(annotationQueryBBox), [annotationQueryBBox]);

  const { data: serverAnnotations = EMPTY_ANNOTATION_SUMMARIES } = useQuery<AnnotationSummary[]>({
    queryKey: ["annotations", "player", annotationQueryBBoxParam ?? "active"],
    enabled: annotationsEnabled,
    refetchInterval: annotationsEnabled ? ANNOTATION_FETCH_REFETCH_INTERVAL_MS : false,
    staleTime: ANNOTATION_FETCH_REFETCH_INTERVAL_MS,
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("active", "true");
      if (annotationQueryBBoxParam) {
        params.set("bbox", annotationQueryBBoxParam);
      }

      const response = await authorizedFetch(`/api/annotations?${params.toString()}`);
      const payload = (await response.json().catch(() => null)) as {
        annotations?: AnnotationResponse[];
        error?: string;
      } | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to load annotations");
      }

      return (payload?.annotations ?? [])
        .map((annotation) => normalizeAnnotationSummary(annotation, "server"))
        .filter((annotation): annotation is AnnotationSummary => Boolean(annotation));
    },
  });



  useEffect(() => {
    activeStreamIdRef.current = activeStreamId;
  }, [activeStreamId]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== LOCAL_ANNOTATIONS_STORAGE_KEY) {
        return;
      }

      setLocalAnnotations(readLocalPlayerAnnotations());
    };

    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  useEffect(() => {
    if (!playbackStream) {
      setIsVideoPlaying(false);
      setOverlayMarkerState(null);
      setAnnotationViewport(null);
      setAnnotationQueryBBox(null);
      annotationTrailRef.current = [];
    }
  }, [playbackStream]);

  useEffect(() => {
    if (!throttledKlv) {
      return;
    }

    const points = collectAnnotationGeoPoints(throttledKlv.klv);
    if (points.length === 0) {
      return;
    }

    const timestamp = throttledKlv.time.getTime();
    const cutoff = timestamp - ANNOTATION_QUERY_TRAIL_WINDOW_MS;
    const retained = annotationTrailRef.current.filter((entry) => entry.timestamp >= cutoff);
    for (const point of points) {
      retained.push({ timestamp, point });
    }
    annotationTrailRef.current = retained;
  }, [throttledKlv]);

  useEffect(() => {
    if (!annotationsEnabled) {
      setAnnotationQueryBBox(null);
      return;
    }

    const refreshAnnotationQueryBBox = () => {
      const currentPoints = collectAnnotationGeoPoints(latestKlvRef.current?.klv ?? null);
      const trailPoints = annotationTrailRef.current.map((entry) => entry.point);
      const telemetryBBox = bboxFromPoints([...trailPoints, ...currentPoints]);
      const viewportBBox = annotationViewport ? ([...annotationViewport.bbox] as GeoBBox) : null;
      const nextBBox = unionBBox(telemetryBBox, viewportBBox);
      const expandedNextBBox = nextBBox ? expandBBox(nextBBox) : null;

      setAnnotationQueryBBox((previousBBox) =>
        bboxesRoughlyEqual(previousBBox, expandedNextBBox) ? previousBBox : expandedNextBBox,
      );
    };

    refreshAnnotationQueryBBox();
    const intervalId = window.setInterval(refreshAnnotationQueryBBox, ANNOTATION_QUERY_REFRESH_INTERVAL_MS);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [annotationViewport, annotationsEnabled, playbackStream?.id]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(BITRATE_RANGE_STORAGE_KEY, bitrateRange);
  }, [bitrateRange]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(BITRATE_REFRESH_STORAGE_KEY, String(bitrateRefreshMs));
  }, [bitrateRefreshMs]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(EVENTS_RANGE_STORAGE_KEY, eventsRange);
  }, [eventsRange]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(EVENTS_REFRESH_STORAGE_KEY, String(eventsRefreshMs));
  }, [eventsRefreshMs]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    persistPlayerOverlayLayout(overlayLayout);
  }, [overlayLayout]);

  useEffect(() => {
    setIsGridEnabled(overlayLayout?.layerEnabledStates?.grid ?? false);
  }, [overlayLayout?.layerEnabledStates?.grid]);

  useEffect(() => {
    setIsRulerEnabled(overlayLayout?.rulerEnabled ?? false);
  }, [overlayLayout?.rulerEnabled]);

  useEffect(() => {
    setIsMarkerEnabled(overlayLayout?.markerEnabled ?? false);
  }, [overlayLayout?.markerEnabled]);

  // useEffect(() => {
  //   if (typeof window === "undefined") {
  //     return;
  //   }
  //   try {
  //     const stored = window.localStorage.getItem(eventsFilterStorageKey);
  //     if (!stored) {
  //       setEventsErrorFilterSettings(createDefaultStreamEventErrorFilterSettings());
  //       return;
  //     }
  //     const parsed = JSON.parse(stored) as Record<string, unknown>;
  //     const next = createDefaultStreamEventErrorFilterSettings();
  //     Object.keys(next).forEach((key) => {
  //       if (typeof parsed[key] === "boolean") {
  //         next[key] = parsed[key] as boolean;
  //       }
  //     });
  //     setEventsErrorFilterSettings(next);
  //   } catch {
  //     setEventsErrorFilterSettings(createDefaultStreamEventErrorFilterSettings());
  //   }
  // }, [eventsFilterStorageKey]);

  useEffect(() => {
    const element = videoContainer;
    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      const { width, height } = entry.contentRect;
      setVideoSize((current) =>
        current.width === width && current.height === height ? current : { width, height },
      );
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [videoContainer]);

  const activeStreams = useMemo(
    () =>
      streams?.filter(
        (stream) =>
          stream.active &&
          !isDataPayload(stream.payloadType) &&
          !isRecastOnlyMode(stream.mode),
      ) ?? [],
    [streams],
  );

  const timelineStream = useMemo(
    () =>
      timelineStreamId
        ? streams?.find((stream) => stream.id === timelineStreamId) ?? null
        : null,
    [streams, timelineStreamId],
  );

  const calendarStream = useMemo(
    () =>
      calendarStreamId
        ? streams?.find((stream) => stream.id === calendarStreamId) ?? null
        : null,
    [streams, calendarStreamId],
  );

  const isTimelineOpen = Boolean(timelineStreamId);
  const isCalendarOpen = Boolean(calendarStreamId);

  const selectedStream = useMemo(() => {
    if (!activeStreamId) return null;
    return streams?.find((s) => s.id === activeStreamId) ?? null;
  }, [activeStreamId, streams]);

  useEffect(() => {
    if (!timelineStreamId) {
      return;
    }
    if (!streams?.some((stream) => stream.id === timelineStreamId)) {
      setTimelineStreamId(null);
    }
  }, [streams, timelineStreamId]);

  useEffect(() => {
    if (!calendarStreamId) {
      return;
    }
    if (!streams?.some((stream) => stream.id === calendarStreamId)) {
      setCalendarStreamId(null);
    }
  }, [streams, calendarStreamId]);

  useEffect(() => {
    if (!isTimelineOpen) {
      return;
    }
    if (!selectedStream) {
      setTimelineStreamId(null);
      return;
    }
    setTimelineStreamId((current) =>
      current === selectedStream.id ? current : selectedStream.id,
    );
  }, [isTimelineOpen, selectedStream]);

  useEffect(() => {
    if (!isCalendarOpen) {
      return;
    }
    if (!selectedStream) {
      setCalendarStreamId(null);
      return;
    }
    setCalendarStreamId((current) =>
      current === selectedStream.id ? current : selectedStream.id,
    );
  }, [isCalendarOpen, selectedStream]);

  const selectableStreams = useMemo(() => streams?.filter((stream) =>
    !isDataPayload(stream.payloadType) && !isRecastOnlyMode(stream.mode) &&
    (stream.active || selectedStreamIds.includes(stream.id) || stream.id === activeStreamId),
  ) ?? [], [activeStreamId, selectedStreamIds, streams]);
  const selectedPlayers = useMemo(() => selectedStreamIds.flatMap((id) => {
    const stream = selectableStreams.find((item) => item.id === id);
    return stream ? [stream] : [];
  }), [selectedStreamIds, selectableStreams]);
  const multiplePlayers = selectedPlayers.length > 1;
  const playerGrid = getPlayerGrid(selectedPlayers.length, playersPerRow);
  // Very large selections can scroll once further shrinking would overlap labels.
  const playerGridHeight = Math.max(gridHeight, playerGrid.rows * 96 + (playerGrid.rows - 1) * 12);

  useEffect(() => { persistPlayersPerRow(playersPerRow); }, [playersPerRow]);

  useEffect(() => {
    const element = gridRef.current;
    if (!element) return;
    const updateHeight = () => {
      const top = Math.max(0, element.getBoundingClientRect().top);
      setGridHeight(Math.max(1, window.innerHeight - top - 24));
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    window.addEventListener("resize", updateHeight);
    return () => { observer.disconnect(); window.removeEventListener("resize", updateHeight); };
  }, [multiplePlayers, isOverlayEditorOpen]);

  const platformsById = useMemo(() => {
    const map = new Map<string, PlatformSummary>();
    if (!platforms) {
      return map;
    }
    for (const platform of platforms) {
      const id = platform.id?.trim();
      if (!id) {
        continue;
      }
      map.set(id, platform);
    }
    return map;
  }, [platforms]);

  const handleOpenStream = useCallback(
    (streamId: string) => {
      const targetStream = streams?.find((s) => s.id === streamId);
      if (!targetStream) {
        console.warn("Stream not found for id", streamId);
        return;
      }

      setPlaybackStream(null);

      setPlaybackStream(targetStream);
    },
    [streams],
  );

  const changeStream = useCallback(
    (id: string | null) => {
      selectionInitializedRef.current = true;
      if (id) setSelectedStreamIds((current) => current.includes(id) ? current : [...current, id]);
      const previousActiveId = activeStreamIdRef.current;
      sceneRun.current += 1;
      setIsVideoPlaying(false);
      setOverlayMarkerState(null);

      setThrottledKlv(null);
      setRawKlvPacket(null);
      latestKlvRef.current = null;
      platformGeometryRef.current = null;
      if (pendingThrottleRef.current) {
        window.clearTimeout(pendingThrottleRef.current);
        pendingThrottleRef.current = null;
      }

      if (previousActiveId) {
        setPlaybackStream((prev) => {
          if (prev && prev.id === previousActiveId) {
            return null;
          }
          return prev;
        });
      }

      if (!id) {
        activeStreamIdRef.current = null;
        setActiveStreamId(null);
        pendingStreamIdRef.current = null;
        return;
      }

      activeStreamIdRef.current = id;
      setActiveStreamId(id);
      pendingStreamIdRef.current = id;
      queueMicrotask(() => {
        if (pendingStreamIdRef.current === id) {
          void handleOpenStream(id);
          pendingStreamIdRef.current = null;
        }
      });
    },
    [handleOpenStream],
  );

  useEffect(() => {
    if (!streams || isStreamsFetching || !isStreamsFetchedAfterMount || isStreamsError || selectionReady) return;

    const stored = readPlayerSelection();
    if (stored) {
      const availableIds = new Set(streams.filter((stream) =>
        !isDataPayload(stream.payloadType) && !isRecastOnlyMode(stream.mode),
      ).map((stream) => stream.id));
      const restored = reconcilePlayerSelection(stored, availableIds);
      persistPlayerSelection(restored);
      if (restored.streamIds.length > 0 || stored.streamIds.length === 0) {
        selectionInitializedRef.current = true;
        setSelectedStreamIds(restored.streamIds);
        if (restored.activeStreamId) changeStream(restored.activeStreamId);
      }
    }
    setSelectionReady(true);
  }, [streams, isStreamsFetching, isStreamsFetchedAfterMount, isStreamsError, selectionReady, changeStream]);

  useEffect(() => {
    if (!selectionReady) return;
    persistPlayerSelection({ streamIds: selectedStreamIds, activeStreamId });
  }, [selectionReady, selectedStreamIds, activeStreamId]);

  useEffect(() => {
    if (!streams || !selectionReady) return;
    const validIds = selectedStreamIds.filter((id) => streams.some((stream) =>
      stream.id === id && !isDataPayload(stream.payloadType) && !isRecastOnlyMode(stream.mode),
    ));
    if (validIds.length === selectedStreamIds.length) return;
    setSelectedStreamIds(validIds);
    if (activeStreamId && !validIds.includes(activeStreamId)) {
      changeStream(validIds[0] ?? null);
      const next = new URLSearchParams(searchParams);
      if (validIds[0]) next.set("streamId", validIds[0]);
      else next.delete("streamId");
      setSearchParams(next, { replace: true });
    }
  }, [streams, selectionReady, selectedStreamIds, activeStreamId, changeStream, searchParams, setSearchParams]);

  useEffect(() => {
    if (!requestedStreamId || !selectionReady) {
      return;
    }

    if (!streams || streams.length === 0) {
      return;
    }

    const requestedStream = streams.find((stream) => stream.id === requestedStreamId);
    if (!requestedStream || isDataPayload(requestedStream.payloadType) || isRecastOnlyMode(requestedStream.mode)) {
      const next = new URLSearchParams(searchParams);
      next.delete("streamId");
      setSearchParams(next, { replace: true });
      return;
    }

    if (activeStreamIdRef.current === requestedStreamId || pendingStreamIdRef.current === requestedStreamId) {
      return;
    }

    void changeStream(requestedStreamId);
  }, [requestedStreamId, selectionReady, streams, changeStream, searchParams, setSearchParams]);

  const playerServerUrl = useMemo(
    () => resolveApiPath("/whep"),
    [resolveApiPath],
  );

  const playbackSensor = useMemo(() => {
    if (!playbackStream?.sensor) return "";
    return playbackStream.sensor.trim();
  }, [playbackStream]);

  const playbackPlatformDetails = useMemo(() => {
    if (!playbackStream?.platformId) {
      return null;
    }
    const id = playbackStream.platformId.trim();
    if (!id) {
      return null;
    }
    return platformsById.get(id) ?? null;
  }, [playbackStream?.platformId, platformsById]);

  const playbackPlatformType = useMemo(() => {
    const resolved = resolvePlatformTypeForMap(playbackPlatformDetails?.type);
    return resolved ?? null;
  }, [playbackPlatformDetails]);

  const playbackPlatformColor = useMemo(() => {
    const normalized = normalizePlatformColor(playbackPlatformDetails?.color);
    return normalized ?? DEFAULT_PLATFORM_COLOR;
  }, [playbackPlatformDetails]);

  const playbackPlatformName = useMemo(() => {
    // First try to get the name from platform details
    if (playbackPlatformDetails?.name?.trim()) {
      return playbackPlatformDetails.name.trim();
    }
    // Then try platformName field on the stream
    if (playbackStream?.platformName?.trim()) {
      return playbackStream.platformName.trim();
    }
    // Last resort: use platform ID or stream ID
    if (playbackStream?.platformId?.trim()) {
      return playbackStream.platformId.trim();
    }
    if (playbackStream?.id?.trim()) {
      return playbackStream.id.trim();
    }
    return "";
  }, [playbackPlatformDetails, playbackStream]);

  const filteredLocalAnnotations = useMemo(
    () =>
      localAnnotations.filter(
        (annotation) => annotation.active && annotationIntersectsBBox(annotation.bbox, annotationQueryBBox),
      ),
    [annotationQueryBBox, localAnnotations],
  );

  const activeAnnotationItems = useMemo(() => {
    const merged = [...serverAnnotations, ...filteredLocalAnnotations];
    const seen = new Set<string>();
    const items: AnnotationRenderItem[] = [];

    for (const annotation of merged) {
      const item = toAnnotationRenderItem(annotation);
      if (!item || seen.has(item.id)) {
        continue;
      }

      seen.add(item.id);
      items.push(item);
    }

    return items;
  }, [filteredLocalAnnotations, serverAnnotations]);

  const playerAnnotations = useMemo(
    () => (annotationsEnabled ? activeAnnotationItems : EMPTY_ANNOTATION_ITEMS),
    [activeAnnotationItems, annotationsEnabled],
  );

  const bitrateChartHeight = useMemo(() => {
    if (videoSize.height > 0) {
      return videoSize.height * 0.2;
    }
    return 160;
  }, [videoSize.height]);
  const isUsingAnalyticsHistory = analyticsEnabled && bitrateRange !== "latest";
  const isUsingEventsAnalyticsHistory = analyticsEnabled && eventsRange !== "latest";

  // Pick a default active stream once streams load
  useEffect(() => {
    if (!selectionReady) return;
    if (!activeStreams.length) {
      const current = activeStreamIdRef.current;
      if (current) {
        const exists = streams?.some((stream) => stream.id === current) ?? false;
        if (exists) {
          return;
        }
        activeStreamIdRef.current = null;
        setActiveStreamId(null);
      }
      pendingStreamIdRef.current = null;
      return;
    }
    if (requestedStreamId) {
      return;
    }
    if (selectedStream || selectionInitializedRef.current) return;
    const nextId = activeStreams[0]?.id;
    if (!nextId) return;
    activeStreamIdRef.current = nextId;
    setActiveStreamId(nextId);
    selectionInitializedRef.current = true;
    setSelectedStreamIds([nextId]);
    queueMicrotask(() => {
      if (activeStreamIdRef.current === nextId) {
        void handleOpenStream(nextId);
      }
    });
  }, [
    activeStreams,
    selectedStream,
    handleOpenStream,
    selectionReady,
    requestedStreamId,
    streams
  ]);

  useEffect(() => {
    if (!selectedStream) {
      setPlaybackStream(null);
      return;
    }
    setPlaybackStream((prev) => {
      if (prev && prev.id === selectedStream.id) {
        return selectedStream;
      }
      return prev;
    });
  }, [selectedStream]);

  // Create (stable) platform geometry ONLY when stream changes. Geometry is a simple point;
  // position + heading will be supplied via platform data updates (update-platform-data messages)
  // so that we avoid re-sending geometry and instead leverage cheaper metadata updates.
  useEffect(() => {
    if (!playbackStream) {
      platformGeometryRef.current = null;
      return;
    }
    const klv = latestKlvRef.current?.klv ?? {};
    const lat = typeof klv['13'] === 'number' ? (klv['13'] as number) : 0;
    const lon = typeof klv['14'] === 'number' ? (klv['14'] as number) : 0;
    const heading = typeof klv['5'] === 'number' ? (klv['5'] as number) : 0;
    const platformType = playbackPlatformType ?? DEFAULT_PLATFORM_TYPE;
    const platformId = playbackStream.platformId?.trim() || playbackStream.id;
    const platformName =
      playbackPlatformDetails?.name?.trim() ||
      playbackStream.platformName?.trim() ||
      playbackStream.platformId?.trim() ||
      playbackStream.id;
    platformGeometryRef.current = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            // Initial coordinates; subsequent movement comes through data-only updates
            coordinates: [lon, lat],
          },
          properties: {
            id: playbackStream.id,
            entity: 'Platform',
            platformType,
            color: playbackPlatformColor,
            status: 'active',
            rotation: heading,
            icon: {             
              size: [48, 48],
              anchor: [24, 24],
            },
            platform: {
              id: platformId,
              name: platformName,
              type: platformType,
              color: playbackPlatformColor,
            },
            frameCenter: {
              color: '#0bf',
              dashArray: [5, 10],
              weight: 1,
              opacity: 0.4,
            },
            footprints: {
              color: 'rgba(25, 0, 252, 1)',
              dashArray: [5, 10],
              weight: 2,
              opacity: 1,
            },
            sensorRadar: {
              online: {
                color: '#0bf',
                dashArray: [5, 10],
                weight: 1,
                opacity: 0.4,
                fillColor: '#0bf',
                fillOpacity: 0.05
              },
              animate: {
                color: '#0bf',
                weight: 0,
                opacity: 0,
                fillColor: '#0bf',
                fillOpacity: 0.2
              },
              step: 2
            },
            antPath: {
              history: 30,
              delay: 1000,
              dashArray: [10, 20],
              weight: 2,
              color: playbackPlatformColor,
              pulseColor: '#FFFFFF',
              paused: false,
              reverse: false,
              hardwareAccelerated: true
            }
          },
        },
      ],
    } as GeoJsonObject;
  }, [playbackStream, playbackPlatformType, playbackPlatformDetails, playbackPlatformColor]);

  // Build platforms array: geometry stable, data object changes with throttledKlv
  const mapPlatforms: MapPlatformDescriptor[] = useMemo(() => {
    if (!playbackStream || !platformGeometryRef.current || !throttledKlv) return [];
    return [
      {
        id: playbackStream.id,
        geojson: platformGeometryRef.current,
        data: {
          time: throttledKlv.time,
          klv: throttledKlv.klv,
          platformType: playbackPlatformType ?? DEFAULT_PLATFORM_TYPE,
          platformColor: playbackPlatformColor,
        }, // triggers update-platform-data when reference changes
      },
    ];
  }, [playbackStream, throttledKlv, playbackPlatformType, playbackPlatformColor]);

  sceneStreamIdentity.current = playbackStream?.id ?? null;
  const sceneSources = useMemo<SceneSource[]>(() => {
    if (!playbackStream?.active || !throttledKlv || !throttledKlv.sceneGeneration) return [];
    return [{id: playbackStream.id, name: playbackPlatformName || playbackStream.id,
      generation: throttledKlv.sceneGeneration, sequence: throttledKlv.sceneSequence ?? 0,
      time: throttledKlv.time.toISOString(), receivedAt: throttledKlv.time.getTime(), fields: throttledKlv.klv,
      running: true, paused: false, live: true}];
  }, [playbackStream, playbackPlatformName, throttledKlv]);
  const sceneWindow = useSceneWindow({sources: sceneSources, theme: theme.palette.mode,
    apiBase: new URL(resolveApiPath('/'), window.location.origin).href,
    userKey: userManagementEnabled ? user?.id || 'signed-out' : 'local',
    accessToken: tokens?.accessToken || '', admin: !userManagementEnabled || user?.role === 'admin',
    refreshAuth: () => authorizedFetch('/api/scene/assets')});

  // Handler for VideoPlayer data receive
  const handleDataReceive = useCallback((packet: { time: Date; klv: unknown }) => {
    if (!packet || typeof packet !== 'object') return;
    const { time, klv } = packet as { time: Date; klv: unknown };
    if (!klv || typeof klv !== 'object') return;
    const rawMap = klv as Record<string, unknown>;
    const sceneMetadata = {sceneSequence: ++sceneSequence.current, sceneGeneration: `${sceneStreamIdentity.current}:${sceneRun.current}`};
    latestKlvRef.current = { time, klv: rawMap, ...sceneMetadata };
    setRawKlvPacket({ time, klv: rawMap, ...sceneMetadata });
    // Throttle KLV updates for map/platform
    setThrottledKlv((prev) => {
      const now = Date.now();
      const elapsed = now - lastThrottleUpdateRef.current;
      if (elapsed >= THROTTLE_INTERVAL_MS) {
        lastThrottleUpdateRef.current = now;
        if (pendingThrottleRef.current) {
          window.clearTimeout(pendingThrottleRef.current);
          pendingThrottleRef.current = null;
        }
        return { time, klv: rawMap, ...sceneMetadata };
      }
      // schedule trailing
      if (pendingThrottleRef.current) {
        window.clearTimeout(pendingThrottleRef.current);
      }
      pendingThrottleRef.current = window.setTimeout(() => {
        lastThrottleUpdateRef.current = Date.now();
        setThrottledKlv({ time, klv: rawMap, ...sceneMetadata });
        pendingThrottleRef.current = null;
      }, Math.max(0, THROTTLE_INTERVAL_MS - elapsed));
      return prev; // keep previous until trailing fires
    });
  }, []);

  const handleCurrentStreamData = useCallback((packet: {time: Date; klv: unknown}) => {
    if (sceneStreamIdentity.current === playbackStream?.id && activeStreamIdRef.current === playbackStream?.id) handleDataReceive(packet);
  }, [handleDataReceive, playbackStream?.id]);

  // Cleanup pending throttle on unmount
  useEffect(() => {
    return () => {
      if (pendingThrottleRef.current) {
        window.clearTimeout(pendingThrottleRef.current);
        pendingThrottleRef.current = null;
      }
    };
  }, []);

  const focusStream = (id: string | null) => {
    if (activeStreamIdRef.current !== id) changeStream(id);
    const next = new URLSearchParams(searchParams);
    if (id) {
      next.set("streamId", id);
    } else {
      next.delete("streamId");
    }
    setSearchParams(next, { replace: true });
  };

  const handleSelectStreamSelect = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const ids = typeof value === "string" ? value.split(",") : value;
    selectionInitializedRef.current = true;
    setSelectedStreamIds(ids);
    const nextFocus = activeStreamId && ids.includes(activeStreamId) ? activeStreamId : ids[0] ?? null;
    focusStream(nextFocus);
  };

  const handleToggleKlvWindow = (_event: MouseEvent<HTMLElement>) => {
    setIsKlvWindowOpen((prev) => !prev);
  };

  const handleKlvWindowClose = () => setIsKlvWindowOpen(false);

  const handleToggleMap = (_event: MouseEvent<HTMLElement>) => {
    setIsMapWindowOpen((prevOpen) => !prevOpen);
  };

  const handleToggleOverlayEditor = useCallback(() => {
    setIsOverlayEditorOpen((current) => !current);
  }, []);

  const handleOverlayLayoutChange = useCallback((layout: StoredPlayerOverlayLayout) => {
    setOverlayLayout(layout);
  }, []);

  const overlayPersistence = useMemo(() => createPlayerOverlayPersistence(
    () => ({ streamId: activeStreamIdRef.current, editing: overlayEditorOpenRef.current }),
    handleOverlayLayoutChange,
  ), [handleOverlayLayoutChange]);
  const handleEditorLayoutChange = useCallback((layout: StoredPlayerOverlayLayout) => {
    overlayPersistence.saveEditor(activeStreamId, layout);
  }, [activeStreamId, overlayPersistence]);

  const handleOverlayMarkerStateChange = useCallback((state: OverlayMarkerSyncState) => {
    setOverlayMarkerState((current) => {
      if (
        current?.enabled === state.enabled &&
        current.master === state.master &&
        current.available === state.available &&
        current.latitude === state.latitude &&
        current.longitude === state.longitude &&
        current.point?.x === state.point?.x &&
        current.point?.y === state.point?.y
      ) {
        return current;
      }
      return state;
    });
  }, []);

  const handleToggleRuler = useCallback(() => {
    setIsRulerEnabled((current) => !current);
  }, []);

  const handleToggleAnnotations = useCallback(() => {
    setOverlayLayout((currentLayout) => {
      const baseLayout = currentLayout ?? createDefaultOverlayLayout(resolveOverlayViewport(videoSize));
      const nextEnabled = !(baseLayout.layerEnabledStates?.annotations === true);

      return {
        ...baseLayout,
        layerEnabledStates: {
          ...(baseLayout.layerEnabledStates ?? {}),
          annotations: nextEnabled,
        },
      };
    });
  }, [videoSize]);

  const handleToggleMarker = useCallback(() => {
    setIsMarkerEnabled((currentEnabled) => {
      const nextEnabled = !currentEnabled;
      const viewport = resolveOverlayViewport(videoSize);
      const centeredPoint = createCenteredMarkerPoint(viewport);

      setOverlayMarkerState(
        nextEnabled
          ? {
              enabled: true,
              master: true,
              available: false,
              point: centeredPoint,
              latitude: null,
              longitude: null,
            }
          : null,
      );

      setOverlayLayout((currentLayout) => {
        const baseLayout = currentLayout ?? createDefaultOverlayLayout(viewport);

        if (!nextEnabled) {
          return {
            ...baseLayout,
            markerEnabled: false,
          };
        }

        return {
          ...baseLayout,
          markerEnabled: true,
          markerMaster: true,
          markerPoint: centeredPoint,
          markerCoordinate: null,
        };
      });

      return nextEnabled;
    });
  }, [videoSize]);

  const handleToggleGrid = useCallback(() => {
    setIsGridEnabled((current) => !current);
  }, []);

  const handleMapMarkerMove = useCallback((position: MapMarkerPosition) => {
    const { lat, lon } = position;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      return;
    }

    setIsMarkerEnabled(true);
    setOverlayMarkerState({
      enabled: true,
      master: false,
      available: true,
      point: null,
      latitude: lat,
      longitude: lon,
    });
    setOverlayLayout((current) => {
      const base = current ?? createDefaultOverlayLayout(resolveOverlayViewport(videoSize));
      return {
        ...base,
        markerEnabled: true,
        markerMaster: false,
        markerPoint: null,
        markerCoordinate: {
          latitude: lat,
          longitude: lon,
        },
      };
    });
  }, [videoSize]);

  const handleMapMarkerToggle = useCallback((event: MapMarkerToggleEvent) => {
    const position = event.position;

    if (!event.enabled) {
      setIsMarkerEnabled(false);
      setOverlayMarkerState(null);
      setOverlayLayout((current) => {
        if (!current) {
          return current;
        }

        return {
          ...current,
          markerEnabled: false,
        };
      });
      return;
    }

    setIsMarkerEnabled(true);
    setOverlayMarkerState({
      enabled: true,
      master: false,
      available: position !== null,
      point: null,
      latitude: position?.lat ?? null,
      longitude: position?.lon ?? null,
    });
    setOverlayLayout((current) => {
      const base = current ?? createDefaultOverlayLayout(resolveOverlayViewport(videoSize));
      return {
        ...base,
        markerEnabled: true,
        markerMaster: false,
        markerPoint: null,
        markerCoordinate: position
          ? {
              latitude: position.lat,
              longitude: position.lon,
            }
          : null,
      };
    });
  }, [videoSize]);

  const mapMarkerMode = useMemo<MapMarkerMode>(() => {
    if (overlayMarkerState?.enabled) {
      return overlayMarkerState.master ? "slave" : "master";
    }

    if (overlayLayout?.markerEnabled) {
      return overlayLayout.markerMaster === false ? "master" : "slave";
    }

    return "slave";
  }, [overlayLayout?.markerEnabled, overlayLayout?.markerMaster, overlayMarkerState]);

  const mapMarkerPosition = useMemo<MapMarkerPosition | null>(() => {
    if (mapMarkerMode === "master") {
      return null;
    }

    if (overlayMarkerState?.enabled) {
      const lat = overlayMarkerState.latitude;
      const lon = overlayMarkerState.longitude;
      if (typeof lat === "number" && Number.isFinite(lat) && typeof lon === "number" && Number.isFinite(lon)) {
        return { lat, lon };
      }
    }

    if (overlayLayout?.markerEnabled) {
      const lat = overlayLayout.markerCoordinate?.latitude;
      const lon = overlayLayout.markerCoordinate?.longitude;
      if (typeof lat === "number" && Number.isFinite(lat) && typeof lon === "number" && Number.isFinite(lon)) {
        return { lat, lon };
      }
    }

    return null;
  }, [mapMarkerMode, overlayLayout?.markerCoordinate, overlayLayout?.markerEnabled, overlayMarkerState]);

  const handleToggleTimeline = useCallback(() => {
    if (!selectedStream) {
      return;
    }
    setTimelineStreamId((current) =>
      current === selectedStream.id ? null : selectedStream.id,
    );
  }, [selectedStream]);

  const handleToggleCalendar = useCallback(() => {
    if (!selectedStream) {
      return;
    }
    setCalendarStreamId((current) =>
      current === selectedStream.id ? null : selectedStream.id,
    );
  }, [selectedStream]);

  const handleToggleBitrateChart = useCallback(() => {
    setIsBitrateChartOpen((prev) => !prev);
  }, []);

  const handleToggleEventsChart = useCallback(() => {
    setIsEventsChartOpen((prev) => !prev);
  }, []);

  const handleBitrateRangeChange = useCallback((event: SelectChangeEvent<string>) => {
    const value = (event.target.value as BitrateRangeOption) ?? "latest";
    const next = BITRATE_RANGE_OPTIONS.some((option) => option.value === value) ? value : "latest";
    setBitrateRange(next);
  }, []);

  const handleBitrateRefreshChange = useCallback((event: SelectChangeEvent<string>) => {
    const parsed = Number.parseInt(event.target.value, 10);
    const next =
      BITRATE_REFRESH_OPTIONS.find((option) => option.value === parsed)?.value ?? BITRATE_REFRESH_OPTIONS[0]?.value ?? 5000;
    setBitrateRefreshMs(next);
  }, []);

  const handleEventsRangeChange = useCallback((event: SelectChangeEvent<string>) => {
    const value = (event.target.value as BitrateRangeOption) ?? "latest";
    const next = BITRATE_RANGE_OPTIONS.some((option) => option.value === value) ? value : "latest";
    setEventsRange(next);
  }, []);

  const handleEventsRefreshChange = useCallback((event: SelectChangeEvent<string>) => {
    const parsed = Number.parseInt(event.target.value, 10);
    const next =
      BITRATE_REFRESH_OPTIONS.find((option) => option.value === parsed)?.value ?? BITRATE_REFRESH_OPTIONS[0]?.value ?? 5000;
    setEventsRefreshMs(next);
  }, []);

  // const handleToggleEventsErrorFilter = useCallback((errorKey: string, enabled: boolean) => {
  //   setEventsErrorFilterSettings((current) => {
  //     const next = { ...current, [errorKey]: enabled };
  //     if (typeof window !== "undefined") {
  //       window.localStorage.setItem(eventsFilterStorageKey, JSON.stringify(next));
  //     }
  //     return next;
  //   });
  // }, [eventsFilterStorageKey]);

  const handleOpenStreamEventsHelp = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }
    void openExternalPath("/help/user-guide/stream-events.html");
  }, []);

  const handlePlayerStateChange = useCallback((state: string) => {
    if (!state) {
      return;
    }
    const normalized = state.toLowerCase();
    if (ACTIVE_PLAYER_STATES.has(normalized)) {
      setIsVideoPlaying(true);
      return;
    }
    if (INACTIVE_PLAYER_STATES.has(normalized)) {
      sceneRun.current += 1;
      setThrottledKlv(null);
      if (pendingThrottleRef.current) {clearTimeout(pendingThrottleRef.current); pendingThrottleRef.current = null;}
      setIsVideoPlaying(false);
    }
  }, []);

  const handleSnapshotClick = useCallback(() => {
    const snapshotCanvas = videoPlayerRef.current?.getSnapshot?.();
    if (!snapshotCanvas) {
      console.warn("Snapshot capture unavailable");
      return;
    }
    try {
      const dataUrl = snapshotCanvas.toDataURL("image/jpeg", 0.8);
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const parts: string[] = [];
      const platformPart =
        sanitizeFileName(playbackStream?.platformName) ??
        sanitizeFileName(playbackPlatformDetails?.name ?? null);
      if (platformPart) {
        parts.push(platformPart);
      }
      const sensorPart = sanitizeFileName(playbackSensor);
      if (sensorPart) {
        parts.push(sensorPart);
      }
      const baseName = parts.length > 0 ? parts.join("-") : SNAPSHOT_FALLBACK_NAME;
      setSnapshotDraft({
        dataUrl,
        defaultName: `${baseName}-${timestamp}`,
        klv: latestKlvRef.current?.klv ?? null,
      });
    } catch (error) {
      console.error("Snapshot capture failed", error);
    }
  }, [playbackPlatformName, playbackSensor]);

  const handleSnapshotCancel = useCallback(() => setSnapshotDraft(null), []);

  const handleSnapshotSave = useCallback(
  async (args: { fileName: string; addGeoMetadata: boolean; format?: "EXIF" | "EXIF/XMP" | "NITF"; metadata?: Record<string, unknown> | null; embedKlv?: boolean }) => {
      if (!snapshotDraft?.dataUrl) {
        return;
      }
      const safeBase = sanitizeFileName(args.fileName) ?? SNAPSHOT_FALLBACK_NAME;
      const fileExtension = args.addGeoMetadata && args.format === "NITF" ? ".ntf" : ".jpg";
      const finalName = `${safeBase}${fileExtension}`;

      try {
        if (!args.addGeoMetadata) {
          // Save as-is
          const link = document.createElement("a");
          link.href = snapshotDraft.dataUrl;
          link.download = finalName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setSnapshotDraft(null);
          return;
        }

        // Call backend to embed metadata
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), 15000);
        const response = await authorizedFetch("/api/image/export", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            imageData: snapshotDraft.dataUrl,
            metadata: args.metadata ?? snapshotDraft.klv ?? {},
            format: args.format ?? "EXIF",
            platformName: playbackPlatformName,
            streamName: playbackSensor,
            embedKlv: args.embedKlv ?? false,
          }),
          signal: controller.signal as AbortSignal,
        });
        window.clearTimeout(timeoutId);
        if (!response.ok) {
          const contentType = response.headers.get("content-type") ?? "";
          const fallback = `Failed to export image (status ${response.status} ${response.statusText || ""})`.trim();
          const rawText = await response.text().catch(() => "");
          let message = fallback;
          if (rawText) {
            if (contentType.includes("application/json")) {
              try {
                const parsed = JSON.parse(rawText) as { error?: unknown };
                if (parsed && typeof parsed.error === "string" && parsed.error.trim()) {
                  message = parsed.error.trim();
                }
              } catch {
                // ignore JSON parse errors and fall back to raw text
              }
            }
            if (message === fallback && rawText.trim()) {
              message = rawText.trim();
            }
          }
          throw new Error(message || fallback);
        }
        const payload = (await response.json().catch(() => null)) as { imageData?: string; mimeType?: string } | null;
        const processedDataUrl = payload?.imageData || snapshotDraft.dataUrl;
        const link = document.createElement("a");
        link.href = processedDataUrl;
        link.download = finalName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setSnapshotDraft(null);
      } catch (error) {
        console.error("Export image failed", error);
        if (error instanceof DOMException && (error as DOMException).name === "AbortError") {
          window.alert("Export timed out. Please try again.");
        } else if (error instanceof Error) {
          window.alert(error.message);
        }
      }
    },
    [snapshotDraft, authorizedFetch, playbackPlatformName, playbackSensor],
  );

  const handleCloseTimeline = useCallback(() => {
    setTimelineStreamId(null);
  }, []);

  const handleCloseCalendar = useCallback(() => {
    setCalendarStreamId(null);
  }, []);

  const handleOverlayEditorSurfaceRef = useCallback((streamId: string, node: HTMLDivElement | null) => {
    setOverlayEditorSurface((current) => node
      ? { streamId, element: node }
      : current?.streamId === streamId ? null : current);
  }, []);

  const noStreamsAvailable =
    !isStreamsLoading &&
    !isStreamsError &&
    selectableStreams.length === 0;

  const helperText = isStreamsError
    ? "Unable to load streams"
    : noStreamsAvailable
      ? "No streams available"
      : null;

  const selectDisabled = isStreamsError || isStreamsLoading || selectableStreams.length === 0;

  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          columnGap: 2,
          rowGap: 1.5,
          alignItems: { xs: "stretch", sm: "center" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexBasis: { xs: "100%", sm: "auto" },
            flexGrow: { xs: 1, sm: 0 },
          }}
        >
          {selectedStream ? (
            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
              <StreamState state={getStreamState(selectedStream.id)} />
            </Box>
          ) : null}
          <Tooltip title="Select stream" sx={{ flexGrow: { xs: 1, sm: 0 } }}>
            <FormControl
              size="small"
              sx={{
                width: { xs: "100%", sm: 300 },
                flexGrow: { xs: 1, sm: 0 },
              }}
              disabled={selectDisabled}
              error={Boolean(helperText)}
            >
              <InputLabel id="stream-select-label">Stream</InputLabel>
              <Select
                labelId="stream-select-label"
                id="stream-select"
                label="Stream"
                multiple
                value={selectedPlayers.map((stream) => stream.id)}
                onChange={handleSelectStreamSelect}
                displayEmpty
                renderValue={(ids) => ids.map((id) => {
                  const stream = selectableStreams.find((item) => item.id === id);
                  return stream?.platformName ? `${stream.platformName} / ${stream.sensor}` : stream?.sensor;
                }).filter(Boolean).join(", ")}
                sx={{ "& .MuiSelect-select": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }}
                MenuProps={{
                  PaperProps: { sx: { maxHeight: 360 } },
                }}
              >
                {/* Real stream items */}
                {!isStreamsLoading &&
                  !isStreamsError &&
                  selectableStreams.length > 0 &&
                  selectableStreams.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      <Checkbox checked={selectedStreamIds.includes(s.id)} size="small" />
                      <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, mr: 1.25 }}>
                        <StreamState state={getStreamState(s.id)} size={12} />
                      </Box>
                      {s.platformName ? `${s.platformName} / ${s.sensor}` : s.sensor}
                    </MenuItem>
                  ))}
              </Select>
              {helperText ? (
                <FormHelperText sx={{ m: 0 }} error>
                  {helperText}
                </FormHelperText>
              ) : null}
            </FormControl>
          </Tooltip>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexBasis: { xs: "100%", sm: "auto" },
          }}
        >
          {multiplePlayers && <PlayerLayoutControl playersPerRow={playersPerRow} onChange={setPlayersPerRow} />}
          <Tooltip title="Klv Window">
            <ToggleButton
              value="klv-window"
              aria-label="klv-window"
              selected={isKlvWindowOpen}
              onChange={handleToggleKlvWindow}
              sx={{ p: 1.25 }}
              color="primary"
            >
              <FormatListNumberedIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>

          <Tooltip title="Map Window">
            <ToggleButton
              aria-label="map-window"
              value="map-window"
              selected={isMapWindowOpen}
              sx={{ p: 1.25 }}
              onChange={handleToggleMap}
            >
              <MapIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="3D View">
            <ToggleButton aria-label="3D View" value="3d-view" selected={sceneWindow.open} sx={{p: 1.25}} onClick={sceneWindow.toggle}>
              <LanguageIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Configure overlay">
            <span style={{ display: "inline-flex" }}>
              <IconButton
                size="small"
                aria-label="Configure overlay"
                color={isOverlayEditorOpen ? "primary" : "default"}
                aria-pressed={isOverlayEditorOpen}
                onClick={handleToggleOverlayEditor}
              >
                <LayersIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Annotations">
            <span style={{ display: "inline-flex" }}>
              <ToggleButton
                value="annotations"
                aria-label="annotations"
                selected={annotationsEnabled}
                disabled={!playbackStream}
                onChange={handleToggleAnnotations}
                sx={{ p: 1.25 }}
                color="primary"
              >
                <EditNoteIcon fontSize="small" />
              </ToggleButton>
            </span>
          </Tooltip>
          <Tooltip title="Perspective grid">
            <span style={{ display: "inline-flex" }}>
              <ToggleButton
                value="grid"
                aria-label="perspective-grid"
                selected={isGridEnabled}
                disabled={!playbackStream}
                onChange={handleToggleGrid}
                sx={{ p: 1.25 }}
                color="primary"
              >
                <GridOnIcon fontSize="small" />
              </ToggleButton>
            </span>
          </Tooltip>
          <Tooltip title="Measure distance">
            <span style={{ display: "inline-flex" }}>
              <ToggleButton
                value="ruler"
                aria-label="measure-distance"
                selected={isRulerEnabled}
                disabled={!playbackStream}
                onChange={handleToggleRuler}
                sx={{ p: 1.25 }}
                color="primary"
              >
                <StraightenIcon fontSize="small" />
              </ToggleButton>
            </span>
          </Tooltip>
          <Tooltip title="Marker">
            <span style={{ display: "inline-flex" }}>
              <ToggleButton
                value="marker"
                aria-label="overlay-marker"
                selected={isMarkerEnabled}
                disabled={!playbackStream}
                onChange={handleToggleMarker}
                sx={{ p: 1.25 }}
                color="primary"
              >
                <PlaceIcon fontSize="small" />
              </ToggleButton>
            </span>
          </Tooltip>
          <Tooltip title="Take a snapshot">
            <span style={{ display: "inline-flex" }}>
              <IconButton
                size="small"
                disabled={!isVideoPlaying}
                onClick={handleSnapshotClick}
              >
                <CameraAltIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {canViewDetectionInfo ? (
            <Tooltip title="Stream detection info">
              <span style={{ display: "inline-flex" }}>
                <IconButton
                  size="small"
                  disabled={!selectedStream}
                  onClick={() => {
                    if (selectedStream) {
                      openDetection(selectedStream);
                    }
                  }}
                >
                  <TroubleshootIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          <Tooltip title="Stream events timeline">
            <span style={{ display: "inline-flex" }}>
              <IconButton
                size="small"
                disabled={!selectedStream}
                color={isTimelineOpen ? "primary" : "default"}
                aria-pressed={isTimelineOpen}
                onClick={handleToggleTimeline}
              >
                <ViewTimelineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Stream events chart">
            <span style={{ display: "inline-flex" }}>
              <IconButton
                size="small"
                disabled={!selectedStream}
                color={isEventsChartOpen ? "primary" : "default"}
                aria-pressed={isEventsChartOpen}
                onClick={handleToggleEventsChart}
              >
                <QueryStatsIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Stream bitrate">
            <span style={{ display: "inline-flex" }}>
              <IconButton
                size="small"
                disabled={!selectedStream}
                color={isBitrateChartOpen ? "primary" : "default"}
                aria-pressed={isBitrateChartOpen}
                onClick={handleToggleBitrateChart}
              >
                <AreaChartIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Stream calendar">
            <span style={{ display: "inline-flex" }}>
              <IconButton
                size="small"
                disabled={!selectedStream}
                color={isCalendarOpen ? "primary" : "default"}
                aria-pressed={isCalendarOpen}
                onClick={handleToggleCalendar}
              >
                <CalendarMonthIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Stack spacing={0.5}>
        {isOverlayEditorOpen && (selectedPlayers.length === 0 || overlayEditorSurface?.streamId === activeStreamId) ? (
          <OverlayEditorSessionPanel
            key={activeStreamId ?? "sample"}
            klvPacket={rawKlvPacket}
            layout={overlayLayout}
            annotations={playerAnnotations}
            onLayoutChange={handleEditorLayoutChange}
            onMarkerStateChange={handleOverlayMarkerStateChange}
            overlaySurfaceElement={overlayEditorSurface?.streamId === activeStreamId ? overlayEditorSurface.element : null}
            gridEnabled={isGridEnabled}
            rulerEnabled={isRulerEnabled}
            markerEnabled={isMarkerEnabled}
          />
        ) : null}
        <Box ref={gridRef} sx={{ width: "100%", minWidth: 0 }}>
          {selectedPlayers.length > 0 ? (
            isMaxClientsExceeded ? (
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  aspectRatio: "16 / 9",
                  bgcolor: (theme) => playerSurfaceColors[theme.palette.mode],
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "error.main",
                  px: 2,
                  textAlign: "center",
                  border: "2px solid",
                  borderColor: "error.main",
                }}
              >
                <Box>
                  <Box sx={{ fontSize: "2rem", mb: 1 }}>⚠️</Box>
                  <Box sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
                    Maximum Clients Exceeded
                  </Box>
                  <Box sx={{ mt: 1, color: "text.secondary", fontSize: "0.9rem" }}>
                    Playback is not available. Please try again later.
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{
                display: "grid", gap: 1.5, minWidth: 0,
                gridTemplateColumns: `repeat(${playerGrid.columns}, minmax(0, 1fr))`,
                alignItems: "start",
                alignContent: "start",
              }}>
                {selectedPlayers.map((stream) => {
                  const focused = stream.id === activeStreamId && stream.id === playbackStream?.id;
                  const receiverBufferMs = resolvePlaybackReceiverBufferMs(stream);
                  const platform = platformsById.get(stream.platformId?.trim() ?? "");
                  return (
                    <PlayerTile key={stream.id} stream={stream} streamState={getStreamState(stream.id)} focused={focused} multiple={multiplePlayers}
                      onFocus={() => focusStream(stream.id)}
                      playerRef={videoPlayerRef} surfaceRef={handleVideoContainer}
                      onData={handleCurrentStreamData}
                      onState={(state) => { if (activeStreamIdRef.current === stream.id) handlePlayerStateChange(state); }}
                      maxHeight={multiplePlayers
                        ? (playerGridHeight - (playerGrid.rows - 1) * 12) / playerGrid.rows - 32
                        : gridHeight}
                      playerProps={{
                        platformName: platform?.name?.trim() || stream.platformName?.trim() || stream.platformId?.trim() || stream.id,
                        sensorName: stream.sensor.trim(),
                        webRtcServerUrl: playerServerUrl,
                        iceServers: iceServers ?? undefined,
                        receiverBufferMs,
                        fastDrainOnStart: receiverBufferMs === 0,
                      }}
                      overlayProps={{
                        layout: overlayLayout, annotations: playerAnnotations,
                        onLayoutChange: (layout) => overlayPersistence.saveViewer(stream.id, layout),
                        onMarkerStateChange: focused ? handleOverlayMarkerStateChange : undefined,
                        gridEnabled: isGridEnabled, rulerEnabled: isRulerEnabled, markerEnabled: isMarkerEnabled,
                        sx: { position: "absolute", inset: 0, zIndex: 2 },
                      }}
                      editing={isOverlayEditorOpen}
                      onEditorSurface={handleOverlayEditorSurfaceRef}
                    />
                  );
                })}
              </Box>
            )
          ) : (
            <Box
              sx={{
                position: "relative",
                width: "100%",
                aspectRatio: "16 / 9",
                bgcolor: (theme) => playerSurfaceColors[theme.palette.mode],
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
                px: 2,
                textAlign: "center",
              }}
            >
              Select a stream to start playback.
            </Box>
          )}
        </Box>

        {isBitrateChartOpen && playbackStream ? (
          <Stack spacing={0.5}>
            {analyticsEnabled ? (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "nowrap", color: (theme) => theme.palette.grey[500] }}
                  >
                    Range
                  </Typography>
                  <FormControl variant="standard" sx={{ minWidth: 160 }}>
                    <Select
                      value={bitrateRange}
                      onChange={handleBitrateRangeChange}
                      sx={{
                        minHeight: 32,
                        color: (theme) => theme.palette.grey[500],
                        "& .MuiSelect-select": { py: 0.5 },
                        "& .MuiSvgIcon-root": { color: (theme) => theme.palette.grey[500] },
                      }}
                    >
                      {BITRATE_RANGE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                {isUsingAnalyticsHistory ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: "nowrap", color: (theme) => theme.palette.grey[500] }}
                    >
                      Update every
                    </Typography>
                    <FormControl variant="standard" sx={{ minWidth: 140 }}>
                      <Select
                        value={String(bitrateRefreshMs)}
                        onChange={handleBitrateRefreshChange}
                        sx={{
                          minHeight: 32,
                          color: (theme) => theme.palette.grey[500],
                          "& .MuiSelect-select": { py: 0.5 },
                          "& .MuiSvgIcon-root": { color: (theme) => theme.palette.grey[500] },
                        }}
                      >
                        {BITRATE_REFRESH_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                ) : null}
              </Box>
            ) : null}
            <Box sx={{ width: "100%", height: bitrateChartHeight }}>
              {isUsingAnalyticsHistory ? (
                <StreamBitrateAnalyticsChart
                  streamId={playbackStream.id}
                  range={bitrateRange as BitrateHistoryRange}
                  refreshMs={bitrateRefreshMs}
                />
              ) : (
                <StreamBitrateChart streamId={playbackStream.id} />
              )}
            </Box>
          </Stack>
        ) : null}

        {isEventsChartOpen && playbackStream ? (
          <Stack spacing={0.5}>
            {analyticsEnabled ? (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {/* <Tooltip title="Filters">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => setIsEventsFilterDialogOpen(true)}
                        aria-label="Configure stream event error filters"
                      >
                        <FilterListIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip> */}
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "nowrap", color: (theme) => theme.palette.grey[500] }}
                  >
                    Range
                  </Typography>
                  <FormControl variant="standard" sx={{ minWidth: 160 }}>
                    <Select
                      value={eventsRange}
                      onChange={handleEventsRangeChange}
                      sx={{
                        minHeight: 32,
                        color: (theme) => theme.palette.grey[500],
                        "& .MuiSelect-select": { py: 0.5 },
                        "& .MuiSvgIcon-root": { color: (theme) => theme.palette.grey[500] },
                      }}
                    >
                      {BITRATE_RANGE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                {isUsingEventsAnalyticsHistory ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: "nowrap", color: (theme) => theme.palette.grey[500] }}
                    >
                      Update every
                    </Typography>
                    <FormControl variant="standard" sx={{ minWidth: 140 }}>
                      <Select
                        value={String(eventsRefreshMs)}
                        onChange={handleEventsRefreshChange}
                        sx={{
                          minHeight: 32,
                          color: (theme) => theme.palette.grey[500],
                          "& .MuiSelect-select": { py: 0.5 },
                          "& .MuiSvgIcon-root": { color: (theme) => theme.palette.grey[500] },
                        }}
                      >
                        {BITRATE_REFRESH_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                ) : null}
              </Box>
            ) : null}
            <Box sx={{ width: "100%", height: bitrateChartHeight }}>
              <StreamEventsAnalyticsChart
                streamId={playbackStream.id}
                range={isUsingEventsAnalyticsHistory ? eventsRange : "latest"}
                refreshMs={eventsRefreshMs}
              />
            </Box>
          </Stack>
        ) : null}
      </Stack>

      <KlvWindow
        open={isKlvWindowOpen}
        onClose={handleKlvWindowClose}
        rawKlvPacket={rawKlvPacket}
      />

      {sceneWindow.error && <Alert severity="error" onClose={sceneWindow.dismissError}>{sceneWindow.error}</Alert>}
      <MapWindow
        open={isMapWindowOpen}
        onClose={() => setIsMapWindowOpen(false)}
        platforms={mapPlatforms}
        annotations={playerAnnotations}
        onViewportChange={setAnnotationViewport}
        tileLayers={serverConfig.mapTileLayers}
        mapTileAccessToken={tokens?.accessToken}
        markerPosition={mapMarkerPosition}
        markerMode={mapMarkerMode}
        onMarkerMove={handleMapMarkerMove}
        onMarkerToggle={handleMapMarkerToggle}
      />
      {/* <StreamEventFiltersDialog
        open={isEventsFilterDialogOpen}
        onClose={() => setIsEventsFilterDialogOpen(false)}
        onOpenHelp={handleOpenStreamEventsHelp}
        errorFilterSettings={eventsErrorFilterSettings}
        onToggleErrorFilter={handleToggleEventsErrorFilter}
      /> */}
      <StreamEventsDrawer open={isTimelineOpen} onClose={handleCloseTimeline} stream={timelineStream} />
      <Drawer anchor="right" open={isCalendarOpen} onClose={handleCloseCalendar} keepMounted>
        <Box
          sx={{
            width: { xs: 280, sm: 320, md: 360 },
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 2,
              mt: 1,
            }}
          >
            <Box>
              <Typography variant="subtitle1">Stream calendar</Typography>
              {calendarStream ? (
                <Typography variant="body2" color="text.secondary">
                  {calendarStream.sensor}
                </Typography>
              ) : null}
            </Box>
            <IconButton
              size="small"
              onClick={handleCloseCalendar}
              aria-label="Close stream calendar"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ flexGrow: 1, overflowY: "auto", px: 2, py: 2 }}>
            {calendarStream ? (
              <StreamCalendar streamId={calendarStream.id} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Select a stream to view its calendar.
              </Typography>
            )}
          </Box>
        </Box>
      </Drawer>
      <StreamInfoDialog
        open={Boolean(detectionStream)}
        onClose={closeDetection}
        streamSensor={detectionStream?.sensor}
        streamId={detectionStream?.id}
        streamState={detectionStream ? getStreamState(detectionStream.id) : undefined}
        isLoading={isDetectionLoading}
        errorMessage={detectionErrorMessage}
        detectionStateLabel={detectionStateLabel}
        showDetectingSpinner={showDetectingSpinner}
        detectedStreamInfo={detectionData?.detectedStreamInfo}
        jsonViewTheme={jsonViewTheme}
      />
      <ExportImageDlg
        open={Boolean(snapshotDraft)}
        imageDataUrl={snapshotDraft?.dataUrl}
        defaultFileName={snapshotDraft?.defaultName}
        klvMetadata={snapshotDraft?.klv ?? null}
        onCancel={handleSnapshotCancel}
        onSave={handleSnapshotSave}
      />
    </Stack>
  );
};

export default PlayerPage;
